/**
 * WebsocketSyncProvider — the missing CLIENT transport for the sync-bridge.
 *
 * The bridge (apps/sync-bridge) is a production binary relay: it forwards every
 * Uint8Array a client sends on `/sync/<siteId>` to the other clients in that
 * room (and across Hyperswarm to peer bridges). It does NO Yjs negotiation, so
 * the SYNC protocol lives here, peer-to-peer over the relay:
 *
 *   - on open, a client sends its STATE VECTOR (a "send me what I'm missing"
 *     request). Peers reply with the diff (`encodeStateAsUpdate(doc, sv)`) —
 *     only the updates that peer lacks, NOT the whole doc, so a 4 KB sitemap
 *     never blows the bridge's 1 MB message cap.
 *   - every local doc update is forwarded as an UPDATE message.
 *   - inbound UPDATEs are applied with origin === this provider, so they are
 *     neither re-broadcast (handleLocalUpdate skips them) nor captured by the
 *     editor's UndoManager (it tracks only FLOW/GRID origins). The
 *     SqliteProvider still persists them (remote edits survive a reload).
 *   - a peer that PERSISTS the doc (the editor's OPFS leader) says so with a
 *     PERSISTED message carrying the state vector it has durably written: in
 *     reply to every sync request, after each write, and as a heartbeat. The
 *     bridge stores nothing, so this is the only evidence a remote edit
 *     (builder-mcp's) is saved anywhere — and that an editor is there at all.
 *   - media bytes never enter the doc (assets live in the editor's OPFS). A
 *     peer without OPFS (builder-mcp) hands one to the editor as BLOB_CHUNKs;
 *     a peer that stores media (the persistence leader, `acceptBlob`) checks
 *     the hash, keeps it, and answers BLOB_STORED. Chunked to stay far under
 *     the bridge's message cap.
 *
 * Offline-first: while disconnected, edits accumulate in the Y.Doc (+ the local
 * SqliteProvider); on reconnect the state-vector handshake re-converges. CRDT
 * merges are idempotent, so duplicate sends against the stateless bridge are
 * safe. This is the BroadcastProvider pattern (useEditorDoc.ts) over a socket;
 * the same class wires into BOTH web and native (global WebSocket exists on
 * both) — the web editor gains real cross-device sync it lacked.
 *
 * No DOM/RN type dependency: a structural WebSocketLike keeps the package
 * platform-agnostic. Tests inject Node's `ws`; web/native use global WebSocket.
 */
import * as Y from "yjs";

const MSG_UPDATE = 0;
const MSG_SYNC_REQUEST = 1;
const MSG_PERSISTED = 2;
const MSG_BLOB_CHUNK = 3;
const MSG_BLOB_STORED = 4;

/** Blob chunk payload: sha256 (32 raw bytes) · total u32 · offset u32 ·
 *  mime length u8 · mime (utf-8) · data. */
const SHA_BYTES = 32;
const CHUNK_HEADER = SHA_BYTES + 4 + 4 + 1;
/** Well under the bridge's 2 MB message cap. */
export const BLOB_CHUNK_BYTES = 512 * 1024;
/** Largest blob a peer assembles (default for `maxBlobBytes`). */
export const MAX_BLOB_BYTES = 32 * 1024 * 1024;
/** A half-received blob is dropped after this long without a chunk. */
const BLOB_IDLE_MS = 30_000;
/** Blobs assembled at once; more are refused, so a peer can't pin memory. */
const MAX_ASSEMBLIES = 4;
const MEDIA_MIME = /^(image|video|audio)\/[\w.+-]+$/;

/** A media blob another peer handed over. */
export interface IncomingBlob {
  /** Hex SHA-256 of `bytes` — already verified. */
  readonly sha256: string;
  readonly mime: string;
  readonly bytes: Uint8Array;
}

interface Assembly {
  readonly mime: string;
  readonly buf: Uint8Array;
  received: number;
  timer: ReturnType<typeof setTimeout>;
}

const hex = (b: Uint8Array): string =>
  Array.from(b, (x) => x.toString(16).padStart(2, "0")).join("");

function unhex(s: string): Uint8Array {
  if (!/^[0-9a-f]{64}$/.test(s)) throw new Error(`not a sha256: ${s}`);
  const out = new Uint8Array(SHA_BYTES);
  for (let i = 0; i < SHA_BYTES; i++)
    out[i] = parseInt(s.slice(i * 2, i * 2 + 2), 16);
  return out;
}

async function sha256Of(bytes: Uint8Array): Promise<string> {
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    bytes as Uint8Array<ArrayBuffer>,
  );
  return hex(new Uint8Array(digest));
}

/** The bridge closes with this when the handshake token expires: fetch a fresh
 *  one and come straight back (no backoff — nothing is wrong). Mirrors
 *  CLOSE_TOKEN_EXPIRED in apps/sync-bridge. */
export const CLOSE_TOKEN_EXPIRED = 4001;

interface WebSocketLike {
  binaryType: string;
  readyState: number;
  send(data: Uint8Array): void;
  close(code?: number, reason?: string): void;
  onopen: (() => void) | null;
  onmessage: ((ev: { data: unknown }) => void) | null;
  onclose: ((ev?: { code?: number; reason?: string }) => void) | null;
  onerror: (() => void) | null;
}

interface WebSocketCtor {
  new (url: string): WebSocketLike;
  readonly OPEN: number;
}

export interface WebsocketSyncProviderOptions {
  /** Base URL of the bridge, e.g. `wss://sync.example.com` (no /sync suffix). */
  readonly url: string;
  readonly siteId: string;
  /** Bearer token for the bridge handshake; resolved before each (re)connect so
   *  a refreshed token is picked up. Omit / resolve null when auth is disabled. */
  readonly getToken?: () => Promise<string | null>;
  /** The bridge refuses a tokenless handshake: when `getToken` yields null
   *  (signed out, not a collaborator), wait and ask again instead of
   *  connecting to be refused. */
  readonly requireToken?: boolean;
  /** Inject a WebSocket implementation (Node `ws` in tests); defaults to the
   *  global WebSocket (browser + React Native). */
  readonly WebSocketImpl?: WebSocketCtor;
  readonly log?: (msg: string, meta?: unknown) => void;
  /** A token-expiry close reconnects at once only if the socket lived at
   *  least this long (default 5 s); a shorter life means the tokens arrive
   *  stale, and the normal backoff applies. Tests shorten it. */
  readonly minLifetimeForFastReconnectMs?: number;
  /**
   * For a peer that persists the doc: the state vector it has durably
   * written, or null when that is unknown (e.g. a write failed). Setting it
   * makes this provider announce PERSISTED on sync requests, on
   * `announcePersisted()`, and every `heartbeatMs`.
   */
  readonly persisted?: () => Uint8Array | null;
  /** PERSISTED heartbeat period (default 10 s). */
  readonly heartbeatMs?: number;
  /**
   * For a peer that stores media (the editor's persistence leader): keep a
   * blob another peer handed over — hash already verified — and resolve true
   * once it is durably stored. Without it, BLOB_CHUNKs are ignored.
   */
  readonly acceptBlob?: (blob: IncomingBlob) => Promise<boolean>;
  /** Largest blob `acceptBlob` is offered (default {@link MAX_BLOB_BYTES}). */
  readonly maxBlobBytes?: number;
}

/** How the last socket ended. A refused handshake (401/403) never opens, so
 *  browsers and `ws` both report it as 1006 — tell "not allowed" from "can't
 *  reach" by asking the token endpoint, not by reading this. */
export interface CloseInfo {
  readonly code: number;
  readonly reason: string;
}

export type ProviderStatus =
  | { readonly kind: "connected" }
  | { readonly kind: "closed"; readonly close: CloseInfo };

function toBytes(data: unknown): Uint8Array {
  if (data instanceof Uint8Array) return data; // Node Buffer is a Uint8Array
  if (data instanceof ArrayBuffer) return new Uint8Array(data);
  if (ArrayBuffer.isView(data)) {
    const v = data as ArrayBufferView;
    return new Uint8Array(v.buffer, v.byteOffset, v.byteLength);
  }
  return new Uint8Array(0);
}

function frame(type: number, payload: Uint8Array): Uint8Array {
  const out = new Uint8Array(payload.length + 1);
  out[0] = type;
  out.set(payload, 1);
  return out;
}

const MAX_BACKOFF_MS = 15_000;
const HEARTBEAT_MS = 10_000;
/** Coalesce a burst of writes into one PERSISTED announcement. */
const ANNOUNCE_DEBOUNCE_MS = 50;
const MIN_LIFETIME_FOR_FAST_RECONNECT_MS = 5_000;

export class WebsocketSyncProvider {
  private ws: WebSocketLike | null = null;
  private shouldConnect = true;
  private attempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly Ctor: WebSocketCtor;
  private readonly statusListeners = new Set<(s: ProviderStatus) => void>();
  private _lastClose: CloseInfo | null = null;
  private openedAt = 0;
  private readonly persistedListeners = new Set<(sv: Uint8Array) => void>();
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private announceTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly assemblies = new Map<string, Assembly>();
  private readonly blobStoredListeners = new Set<
    (sha256: string, stored: boolean) => void
  >();

  constructor(
    private readonly ydoc: Y.Doc,
    private readonly opts: WebsocketSyncProviderOptions,
  ) {
    this.Ctor =
      opts.WebSocketImpl ??
      (globalThis as unknown as { WebSocket?: WebSocketCtor }).WebSocket!;
    this.ydoc.on("update", this.handleLocalUpdate);
    void this.connect();
  }

  /** True once a socket is OPEN. */
  get connected(): boolean {
    return this.ws !== null && this.ws.readyState === this.Ctor.OPEN;
  }

  /** How the most recent socket closed; null until one has. */
  get lastClose(): CloseInfo | null {
    return this._lastClose;
  }

  /** Subscribe to connect/close transitions. Returns the unsubscribe. */
  onStatus(listener: (s: ProviderStatus) => void): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  /**
   * Subscribe to peers' PERSISTED announcements: each carries a state vector
   * that peer has durably written. Returns the unsubscribe.
   */
  onPersisted(listener: (sv: Uint8Array) => void): () => void {
    this.persistedListeners.add(listener);
    return () => this.persistedListeners.delete(listener);
  }

  /**
   * Hand a media blob to the peers that store media, in chunks. Watch
   * {@link onBlobStored} for the answer: nothing is kept until a peer says so.
   */
  pushBlob(sha256: string, mime: string, bytes: Uint8Array): void {
    const sha = unhex(sha256);
    const mimeBytes = new TextEncoder().encode(mime);
    if (mimeBytes.length > 255) throw new Error("mime too long");
    const total = bytes.length;
    let offset = 0;
    do {
      const data = bytes.subarray(offset, offset + BLOB_CHUNK_BYTES);
      const payload = new Uint8Array(
        CHUNK_HEADER + mimeBytes.length + data.length,
      );
      const view = new DataView(payload.buffer);
      payload.set(sha, 0);
      view.setUint32(SHA_BYTES, total);
      view.setUint32(SHA_BYTES + 4, offset);
      view.setUint8(SHA_BYTES + 8, mimeBytes.length);
      payload.set(mimeBytes, CHUNK_HEADER);
      payload.set(data, CHUNK_HEADER + mimeBytes.length);
      this.send(MSG_BLOB_CHUNK, payload);
      offset += data.length;
    } while (offset < total);
  }

  /** Subscribe to peers' answers to pushed blobs. Returns the unsubscribe. */
  onBlobStored(
    listener: (sha256: string, stored: boolean) => void,
  ): () => void {
    this.blobStoredListeners.add(listener);
    return () => this.blobStoredListeners.delete(listener);
  }

  private handleBlobChunk(payload: Uint8Array): void {
    const accept = this.opts.acceptBlob;
    if (!accept || payload.length < CHUNK_HEADER) return;
    const view = new DataView(
      payload.buffer,
      payload.byteOffset,
      payload.byteLength,
    );
    const shaRaw = payload.subarray(0, SHA_BYTES);
    const sha = hex(shaRaw);
    const total = view.getUint32(SHA_BYTES);
    const offset = view.getUint32(SHA_BYTES + 4);
    const mimeLen = view.getUint8(SHA_BYTES + 8);
    const mime = new TextDecoder().decode(
      payload.subarray(CHUNK_HEADER, CHUNK_HEADER + mimeLen),
    );
    const data = payload.subarray(CHUNK_HEADER + mimeLen);
    const refuse = () => {
      this.dropAssembly(sha);
      this.send(MSG_BLOB_STORED, new Uint8Array([...shaRaw, 0]));
    };

    let a = this.assemblies.get(sha);
    if (offset === 0) {
      // A (re)start: a sender that retries begins again from the top.
      this.dropAssembly(sha);
      a = undefined;
      if (
        !MEDIA_MIME.test(mime) ||
        total > (this.opts.maxBlobBytes ?? MAX_BLOB_BYTES) ||
        this.assemblies.size >= MAX_ASSEMBLIES
      )
        return refuse();
      a = {
        mime,
        buf: new Uint8Array(total),
        received: 0,
        timer: setTimeout(() => this.dropAssembly(sha), BLOB_IDLE_MS),
      };
      this.assemblies.set(sha, a);
    }
    // Chunks arrive in order over one relay; anything else is a torn send.
    if (!a) return;
    if (
      offset !== a.received ||
      mime !== a.mime ||
      offset + data.length > a.buf.length
    )
      return refuse();
    a.buf.set(data, offset);
    a.received += data.length;
    clearTimeout(a.timer);
    a.timer = setTimeout(() => this.dropAssembly(sha), BLOB_IDLE_MS);
    if (a.received < a.buf.length) return;

    this.dropAssembly(sha);
    const bytes = a.buf;
    void (async () => {
      let stored = false;
      try {
        stored =
          (await sha256Of(bytes)) === sha &&
          (await accept({ sha256: sha, mime, bytes }));
      } catch (e) {
        this.opts.log?.("blob store failed", e);
      }
      this.send(MSG_BLOB_STORED, new Uint8Array([...shaRaw, stored ? 1 : 0]));
    })();
  }

  private dropAssembly(sha: string): void {
    const a = this.assemblies.get(sha);
    if (!a) return;
    clearTimeout(a.timer);
    this.assemblies.delete(sha);
  }

  /** Tell peers what is persisted now (debounced). No-op without `persisted`. */
  announcePersisted(): void {
    if (!this.opts.persisted || this.announceTimer) return;
    this.announceTimer = setTimeout(() => {
      this.announceTimer = null;
      this.sendPersisted();
    }, ANNOUNCE_DEBOUNCE_MS);
  }

  private sendPersisted(): void {
    const sv = this.opts.persisted?.();
    if (sv) this.send(MSG_PERSISTED, sv);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
  }

  private emit(s: ProviderStatus): void {
    for (const l of this.statusListeners) {
      try {
        l(s);
      } catch {
        /* a listener's failure is its own */
      }
    }
  }

  private async connect(): Promise<void> {
    if (!this.shouldConnect) return;
    let token: string | null = null;
    try {
      token = (await this.opts.getToken?.()) ?? null;
    } catch {
      // token fetch failed (e.g. offline / not authed) → retry later
      this.scheduleReconnect();
      return;
    }
    if (!this.shouldConnect) return;
    if (!token && this.opts.requireToken) {
      this.scheduleReconnect();
      return;
    }

    const sep = this.opts.url.includes("?") ? "&" : "?";
    const q = token ? `${sep}token=${encodeURIComponent(token)}` : "";
    const url = `${this.opts.url}/sync/${encodeURIComponent(this.opts.siteId)}${q}`;

    let ws: WebSocketLike;
    try {
      ws = new this.Ctor(url);
    } catch (e) {
      this.opts.log?.("ws construct failed", e);
      this.scheduleReconnect();
      return;
    }
    ws.binaryType = "arraybuffer";
    this.ws = ws;

    ws.onopen = () => {
      this.attempts = 0;
      this.opts.log?.("ws open", { siteId: this.opts.siteId });
      this.openedAt = Date.now();
      this.emit({ kind: "connected" });
      // Ask peers for whatever this doc is missing.
      this.send(MSG_SYNC_REQUEST, Y.encodeStateVector(this.ydoc));
      if (this.opts.persisted) {
        this.sendPersisted();
        this.stopHeartbeat();
        this.heartbeat = setInterval(
          () => this.sendPersisted(),
          this.opts.heartbeatMs ?? HEARTBEAT_MS,
        );
      }
    };
    ws.onmessage = (ev) => this.handleMessage(toBytes(ev.data));
    ws.onclose = (ev) => {
      // A socket we already replaced (or dropped in destroy) says nothing
      // about the current connection.
      if (this.ws !== ws) return;
      this.ws = null;
      this.stopHeartbeat();
      const close = { code: ev?.code ?? 1006, reason: ev?.reason ?? "" };
      this._lastClose = close;
      this.emit({ kind: "closed", close });
      // Expiry is routine, so reconnect at once — unless the socket barely
      // lived, which means the tokens themselves arrive stale: back off
      // rather than spin.
      const routine =
        close.code === CLOSE_TOKEN_EXPIRED &&
        Date.now() - this.openedAt >=
          (this.opts.minLifetimeForFastReconnectMs ??
            MIN_LIFETIME_FOR_FAST_RECONNECT_MS);
      this.scheduleReconnect(routine ? 0 : undefined);
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    };
  }

  private handleMessage(bytes: Uint8Array): void {
    if (bytes.length === 0) return;
    const type = bytes[0];
    const payload = bytes.subarray(1);
    if (type === MSG_UPDATE) {
      Y.applyUpdate(this.ydoc, payload, this);
    } else if (type === MSG_SYNC_REQUEST) {
      // A peer's state vector → reply with the diff they're missing.
      const diff = Y.encodeStateAsUpdate(this.ydoc, payload);
      if (diff.length > 2) this.send(MSG_UPDATE, diff); // >2 ⇒ not the empty update
      // After the diff, so the asker holds what we claim is saved.
      this.sendPersisted();
    } else if (type === MSG_PERSISTED) {
      for (const l of this.persistedListeners) {
        try {
          l(payload);
        } catch {
          /* a listener's failure is its own */
        }
      }
    } else if (type === MSG_BLOB_CHUNK) {
      this.handleBlobChunk(payload);
    } else if (type === MSG_BLOB_STORED && payload.length === SHA_BYTES + 1) {
      const sha = hex(payload.subarray(0, SHA_BYTES));
      const stored = payload[SHA_BYTES] === 1;
      for (const l of this.blobStoredListeners) {
        try {
          l(sha, stored);
        } catch {
          /* a listener's failure is its own */
        }
      }
    }
  }

  private handleLocalUpdate = (update: Uint8Array, origin: unknown): void => {
    // Skip updates we just applied from a peer (origin === this) — else we echo.
    if (origin === this) return;
    this.send(MSG_UPDATE, update);
  };

  private send(type: number, payload: Uint8Array): void {
    if (this.ws && this.ws.readyState === this.Ctor.OPEN) {
      try {
        this.ws.send(frame(type, payload));
      } catch (e) {
        this.opts.log?.("ws send failed", e);
      }
    }
  }

  /** `delayMs` overrides the backoff (0 = now, for an expected close). */
  private scheduleReconnect(delayMs?: number): void {
    if (!this.shouldConnect || this.reconnectTimer) return;
    let delay: number;
    if (delayMs !== undefined) {
      delay = delayMs;
    } else {
      const backoff = Math.min(1000 * 2 ** this.attempts, MAX_BACKOFF_MS);
      delay = backoff + Math.random() * 1000; // jitter
      this.attempts += 1;
    }
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, delay);
  }

  destroy(): void {
    this.shouldConnect = false;
    this.stopHeartbeat();
    if (this.announceTimer) {
      clearTimeout(this.announceTimer);
      this.announceTimer = null;
    }
    this.ydoc.off("update", this.handleLocalUpdate);
    for (const sha of [...this.assemblies.keys()]) this.dropAssembly(sha);
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    try {
      this.ws?.close();
    } catch {
      /* ignore */
    }
    this.ws = null;
  }
}
